import React, { useState } from 'react'
import { Box, Button, CircularProgress, Paper, Typography, Alert, Snackbar } from '@mui/material'
import { Event, CalendarMonth } from '@mui/icons-material'
import { httpsCallable } from 'firebase/functions'
import { firebase_functions } from '../../../../common/firebaseConfig'
import { useApplicationSuiteContext } from '../../../../contexts/ApplicationSuiteProvider'

export interface WebinarRegistrationPromptProps {
  categoryName: string
  description?: string
  schedule?: string
  webinarId: string
  registrationUrl?: string
  onRegistrationSuccess: () => void
}

const WebinarRegistrationPrompt: React.FC<WebinarRegistrationPromptProps> = ({
  categoryName,
  description,
  schedule,
  webinarId,
  registrationUrl,
  onRegistrationSuccess,
}) => {
  const [isRegistering, setIsRegistering] = useState(false)
  const [registrationError, setRegistrationError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const { usermeta } = useApplicationSuiteContext()

  const isMastermindWebinar = categoryName.includes('Mastermind')

  const handleRegisterClick = async () => {
    if (!webinarId) {
      setRegistrationError('Missing required information for registration')
      return
    }

    setIsRegistering(true)
    setRegistrationError(null)

    try {
      const addWebinarRegistrantFn = httpsCallable(firebase_functions, 'addWebinarRegistrant')
      const result = await addWebinarRegistrantFn({
        webinarId,
        email: usermeta?.email,
        firstName: usermeta?.firstName,
        lastName: usermeta?.lastName,
      })

      interface RegistrationResponse {
        success: boolean
        error?: string | { message: string }
        result?: any
      }

      const response = result.data as RegistrationResponse

      if (response.success) {
        setSuccessMessage(`You've been registered for ${categoryName} webinars!`)
        setTimeout(() => {
          onRegistrationSuccess()
        }, 3000)
      } else {
        if (
          response.error &&
          typeof response.error === 'object' &&
          'message' in response.error &&
          response.error.message.includes('already registered')
        ) {
          setSuccessMessage(`You're already registered for ${categoryName} webinars!`)
        } else {
          setRegistrationError(
            typeof response.error === 'string'
              ? response.error
              : response.error && typeof response.error === 'object' && 'message' in response.error
                ? response.error.message
                : 'Unknown error occurred'
          )
        }
      }
    } catch (error: any) {
      console.error('Error registering for webinar:', error)
      setRegistrationError(error.message || 'An error occurred while registering for the webinar')
    } finally {
      setIsRegistering(false)
    }
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        textAlign: 'center',
        my: 2,
        backgroundColor: 'rgba(0, 0, 0, 0.02)',
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
        <CalendarMonth color="primary" sx={{ fontSize: 40 }} />

        <Typography variant="h6" color="primary" fontWeight="medium">
          {isMastermindWebinar
            ? `${categoryName} Information`
            : `Register for ${categoryName} Webinars`}
        </Typography>

        <Typography variant="body2">
          {isMastermindWebinar
            ? `${categoryName} sessions are exclusive and registration is managed manually. If you're interested in participating, please contact support for more information.`
            : description ||
              `You have access to ${categoryName} webinars with your current subscription. Register now to receive notifications about upcoming sessions.`}
        </Typography>

        {schedule && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            Usually scheduled: {schedule}
          </Typography>
        )}

        {registrationError && (
          <Alert severity="error" sx={{ width: '100%' }}>
            {registrationError}
          </Alert>
        )}

        {!isMastermindWebinar &&
          (registrationUrl ? (
            <Button
              variant="contained"
              color="primary"
              href={registrationUrl}
              component="a"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Event />}
              sx={{ mt: 1 }}
            >
              Register for Webinar
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={handleRegisterClick}
              startIcon={isRegistering ? <CircularProgress size={20} color="inherit" /> : <Event />}
              disabled={isRegistering}
              sx={{ mt: 1 }}
            >
              {isRegistering ? 'Registering...' : 'Register for Webinar'}
            </Button>
          ))}
      </Box>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Paper>
  )
}

export default WebinarRegistrationPrompt
